import { create } from 'zustand';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useEncryptionStore } from './encryptionStore';
import { useTeamStore } from './teamStore';
import {
  setSecureItem,
  getSecureItem,
  deleteSecureItem,
} from '../utils/secureTokenStorage';
import { captureException } from '../observability/sentry';
import { getUserIdFromToken } from '../utils/jwt';
import { broadcastSessionChange } from '../utils/sessionBroadcast';
import { queryClient } from '../api/queryClient';
import { endIdpSession, revokeRefreshToken } from '../auth/oidc';
import { suppressAutoLogin } from '../auth/autoLogin';
import {
  TransientSessionError,
  beginSignOut as beginWebSignOut,
  purgeLegacyTokens,
  refresh as refreshWebSession,
  signOut as signOutWebSession,
} from '../auth/webSession';

// On web the session is cookie-brokered (src/auth/webSession.ts): the access
// token and session id live in this store's memory and nowhere else, and the
// token keys below are only ever DELETED there, never written.
const IS_WEB = Platform.OS === 'web';

const ACCESS_TOKEN_KEY = '@cynosure/access_token';
const REFRESH_TOKEN_KEY = '@cynosure/refresh_token';
// Kept only to pass as `id_token_hint` when ending the IdP session at logout.
// Without it RP-initiated logout shows a confirmation interstitial mid-logout,
// which reads like a fault. Never sent to Cynosure's API — the access token is
// what authorises requests.
const ID_TOKEN_KEY = '@cynosure/id_token';
const USER_KEY = '@cynosure/user';
const LAST_EMAIL_KEY = '@cynosure/last_email';

/**
 * The sign-out already running, if any.
 *
 * ⚠️  EVERY WEB SIGN-OUT USED TO RUN TWICE, AND THE SECOND ONE WON. `logout`
 *     clears the tokens and then calls `queryClient.clear()`, which makes the
 *     mounted queries refetch — with no access token, so they 401, so
 *     `api/client.ts` takes its `if (!refreshToken) await logout()` branch and
 *     starts a SECOND sign-out. That one reads an already-emptied store, so it
 *     reaches `endIdpSession(null)` with no `id_token_hint` and no
 *     `logout_hint`, and its `location.assign` supersedes the first one's.
 *     ZITADEL, unable to name a session to end, hands the browser to its
 *     Login-v2 session picker — which redirects nowhere on its own.
 *
 *     Measured in production on 2026-08-29: three 401s at 22:13:51.472, then
 *     `end_session` at .489 (with hints) and again at .528 (without), then the
 *     picker at .562. Every sign-out for fourteen days looked like this, and
 *     not one of them reached the IdP carrying a `logout_hint`.
 *
 * The FIRST caller is the real one — it is the only one still holding the ID
 * token — so later callers join it rather than starting their own.
 */
let signOutInFlight: Promise<void> | null = null;

// Set by `logoutSessionEnded` for the duration of the sign-out it starts: the
// A3RN session already ended elsewhere, so the sign-out revokes the refresh
// token instead of visiting end_session (identity-architecture §6).
let sessionEndedElsewhere = false;

// Set by `forgetSessionLocally` (web): another tab already signed out or signed
// in as someone else, so this tab clears itself and contacts nobody. Calling
// /web/logout here could revoke the session the OTHER tab just created.
let localOnly = false;

/**
 * The web leg of every sign-out, run LAST because it can navigate away.
 *
 * The API clears the cookies and revokes the refresh token; the browser then
 * visits the IdP's end_session, unless the A3RN session already ended
 * elsewhere (nothing to end) or another tab did the signing out.
 */
async function finishWebSignOut(): Promise<void> {
  if (localOnly) return;
  const endSessionUrl = await signOutWebSession();
  if (!sessionEndedElsewhere && endSessionUrl) window.location.assign(endSessionUrl);
}

/**
 * Record a sign-out as in flight, so concurrent callers join it.
 *
 * ⚠️  WEB DELIBERATELY NEVER CLEARS THIS. `endIdpSession` hands the browser to
 *     the IdP with `location.assign` and resolves immediately, while the page
 *     is still on screen and still able to fire the requests that trigger a
 *     second sign-out. Clearing on completion would re-open the window this
 *     guard exists to close.
 *
 *     Native must clear it: `WebBrowser.openAuthSessionAsync` genuinely
 *     finishes, the app stays alive, and a user who signs in again has to be
 *     able to sign out again.
 */
function trackSignOut(run: Promise<void>): Promise<void> {
  signOutInFlight = run;
  if (Platform.OS !== 'web') {
    const release = () => {
      if (signOutInFlight === run) signOutInFlight = null;
    };
    // `then(release, release)` rather than `finally`: a rejected sign-out must
    // still release the guard, and must not surface as an unhandled rejection
    // from this bookkeeping call.
    run.then(release, release);
  }
  return run;
}

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  email_verified: boolean;
  e2ee_setup_complete: boolean;
  is_admin: boolean;
  created_at: string;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  idToken: string | null;
  /** Web only: the A3RN session id, sent as X-IdP-Session. Native reads it from idToken. */
  sessionId: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  setTokens: (
    accessToken: string,
    refreshToken: string | null,
    idToken?: string | null,
  ) => Promise<void>;
  /** Web only: adopt a cookie-brokered session, held in memory. */
  setWebSession: (accessToken: string, sessionId: string | null) => void;
  setUser: (user: AuthUser) => Promise<void>;
  logout: () => Promise<void>;
  /** Web only: another tab signed out or switched user; clear this tab, contact nobody. */
  forgetSessionLocally: () => Promise<void>;
  // The A3RN session already ended elsewhere (back-channel logout). A separate
  // method rather than an option on `logout`, because screens pass `logout`
  // straight to onPress and it would receive the press event.
  logoutSessionEnded: () => Promise<void>;
  logoutAndForgetDevice: () => Promise<void>;
  initialize: () => Promise<void>;
  ensureCleanSessionForUser: (newUserId: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  refreshToken: null,
  idToken: null,
  sessionId: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,

  setWebSession: (accessToken: string, sessionId: string | null) => {
    set({ accessToken, sessionId, refreshToken: null, idToken: null, isAuthenticated: true });
    broadcastSessionChange(getUserIdFromToken(accessToken));
  },

  setTokens: async (
    accessToken: string,
    refreshToken: string | null,
    idToken?: string | null,
  ) => {
    try {
      const saves: Promise<void>[] = [setSecureItem(ACCESS_TOKEN_KEY, accessToken)];
      if (idToken) saves.push(setSecureItem(ID_TOKEN_KEY, idToken));
      if (refreshToken) {
        saves.push(setSecureItem(REFRESH_TOKEN_KEY, refreshToken));
      } else {
        // Enrollment token — clear any stale refresh token
        saves.push(deleteSecureItem(REFRESH_TOKEN_KEY));
      }
      await Promise.all(saves);
      set({
        accessToken,
        refreshToken,
        // A refresh DOES carry an id_token and the client now passes it, so
        // this normally overwrites with a fresh one. The skip-when-absent
        // branch is the fallback for a provider that returns none: losing the
        // id_token silently disables RP-initiated logout, which is how logout
        // ended up stopping at the provider's session picker.
        ...(idToken ? { idToken } : {}),
        isAuthenticated: true,
      });

      // Notify other browser tabs that the session changed
      broadcastSessionChange(getUserIdFromToken(accessToken));
    } catch (error) {
      console.error('Failed to save tokens:', error);
      captureException(error, { context: 'authStore.setTokens' });
    }
  },

  setUser: async (user: AuthUser) => {
    try {
      await AsyncStorage.setItem(USER_KEY, JSON.stringify(user));
      set({ user });
    } catch (error) {
      console.error('Failed to save user:', error);
      captureException(error, { context: 'authStore.setUser' });
    }
  },

  logout: async () => {
    // ⚠️  JOIN AN IN-FLIGHT SIGN-OUT, NEVER START A SECOND ONE. The callers
    //     that arrive second — the 401 interceptor in `api/client.ts`, the
    //     cross-tab handler and the profile-load safety net in
    //     `app/_layout.tsx` — reach this after the store has been emptied, so
    //     the sign-out they would start carries no `id_token_hint`. See
    //     `signOutInFlight`.
    if (signOutInFlight) return signOutInFlight;

    const signOut = async () => {
      // ⚠️  FIRST, BEFORE `isAuthenticated` IS CLEARED BELOW. That clearing makes
      //     the root guard route to `/(auth)/login`, so the sign-in screen mounts
      //     while this function is still running — and on web that screen asks
      //     whether it may redirect to the IdP. Asked before the flag exists, it
      //     answers yes and starts an /authorize navigation that races the
      //     end_session one this function is about to perform. The /authorize leg
      //     won on 2026-08-29 and flow_55 caught it: Sign Out landed the user on
      //     the hosted login instead of the app's own sign-in screen.
      suppressAutoLogin();
      // Equally first on web: from here no refresh may mint a session, or the
      // refetches `queryClient.clear()` triggers below could put a fresh cookie
      // back behind this sign-out (src/auth/webSession.ts, signOut).
      if (IS_WEB && !localOnly) beginWebSignOut();

      // Captured BEFORE anything is cleared: it is the id_token_hint that lets
      // the IdP end the right session without prompting.
      const idToken = get().idToken;
      const refreshToken = get().refreshToken;
      try {
        await Promise.all([
          deleteSecureItem(ACCESS_TOKEN_KEY),
          deleteSecureItem(REFRESH_TOKEN_KEY),
          deleteSecureItem(ID_TOKEN_KEY),
          AsyncStorage.removeItem(USER_KEY),
        ]);
      } catch (error) {
        console.error('Failed to clear storage:', error);
        captureException(error, { context: 'authStore.logout' });
      }
      // Soft logout: clear in-memory state but preserve the encryption key
      // on device so the same user can re-login without entering recovery phrase.
      useEncryptionStore.getState().clearSession();
      useTeamStore.getState().clearTeams();
      // Clear query cache so a subsequent login doesn't serve stale data
      // from a different user (activities, goals, etc. contain team IDs).
      //
      // ⚠️  THIS IS WHAT MAKES THE MOUNTED QUERIES REFETCH INTO A 401, and that
      //     401 is what used to start a second sign-out. Cancelling first drops
      //     whatever is already in flight; it does NOT stop an observer
      //     refetching after the cache empties, so it reduces the noise rather
      //     than removing it. The guard above is what makes it harmless.
      queryClient.cancelQueries();
      queryClient.clear();
      AsyncStorage.removeItem('@cynosure/react-query-cache').catch(() => {});
      set({
        accessToken: null,
        refreshToken: null,
        idToken: null,
        sessionId: null,
        user: null,
        isAuthenticated: false,
      });

      // Notify other browser tabs that the session ended — unless this tab is
      // itself reacting to such a notice.
      if (!localOnly) broadcastSessionChange(null);

      if (IS_WEB) {
        await finishWebSignOut();
        return;
      }

      // LAST, and it navigates away on web — nothing after it runs. Clearing
      // local state first means a user who closes the tab mid-redirect is still
      // logged out of the app.
      //
      // Unless the session already ended elsewhere: then there is nothing to
      // end, the visit could stop at ZITADEL's session picker, and what is left
      // to do is revoke the refresh token ZITADEL kept alive.
      if (sessionEndedElsewhere) {
        await revokeRefreshToken(refreshToken);
      } else {
        await endIdpSession(idToken);
      }
    };

    return trackSignOut(signOut());
  },

  forgetSessionLocally: async () => {
    localOnly = true;
    try {
      await get().logout();
    } finally {
      localOnly = false;
    }
  },

  logoutSessionEnded: async () => {
    sessionEndedElsewhere = true;
    try {
      await get().logout();
    } finally {
      sessionEndedElsewhere = false;
    }
  },

  logoutAndForgetDevice: async () => {
    // ⚠️  DOES NOT JOIN AN IN-FLIGHT SIGN-OUT, unlike `logout`. Joining would
    //     silently skip forgetting the device — the one thing this function
    //     exists to do that the soft path does not — if a background 401 had
    //     already started an ordinary sign-out. It registers itself instead, so
    //     anything arriving after it joins, and the duplicate `endIdpSession`
    //     that the rare overlap produces is absorbed by the guard in
    //     `auth/oidc.ts`.
    const signOut = async () => {
      // First, for the reason spelled out in `logout` — the sign-in screen mounts
      // mid-function and must not redirect out from under this.
      suppressAutoLogin();
      if (IS_WEB) beginWebSignOut();

      // Hard logout: delete the encryption key from device storage.
      // User will need their recovery phrase to sign in again.
      const idToken = get().idToken;
      try {
        // The user id is passed explicitly: `currentUserId` may already be
        // null here, and the store must not fall back to an unscoped slot.
        await useEncryptionStore.getState().forgetDevice(get().user?.id);
      } catch (error) {
        console.error('Failed to forget device encryption:', error);
        captureException(error, { context: 'authStore.logoutAndForgetDevice.encryption' });
      }
      try {
        await Promise.all([
          deleteSecureItem(ACCESS_TOKEN_KEY),
          deleteSecureItem(REFRESH_TOKEN_KEY),
          deleteSecureItem(ID_TOKEN_KEY),
          AsyncStorage.removeItem(USER_KEY),
          AsyncStorage.removeItem(LAST_EMAIL_KEY),
        ]);
      } catch (error) {
        console.error('Failed to clear storage:', error);
        captureException(error, { context: 'authStore.logoutAndForgetDevice.storage' });
      }
      useTeamStore.getState().clearTeams();
      // Cancelled before cleared, for the reason given in `logout`.
      queryClient.cancelQueries();
      queryClient.clear();
      AsyncStorage.removeItem('@cynosure/react-query-cache').catch(() => {});
      set({
        accessToken: null,
        refreshToken: null,
        idToken: null,
        sessionId: null,
        user: null,
        isAuthenticated: false,
      });

      // Notify other browser tabs that the session ended
      broadcastSessionChange(null);

      // The hard logout needs this MORE than the soft one, not less: "forget this
      // device" that leaves a live IdP session behind is the opposite of what the
      // button says.
      if (IS_WEB) {
        await finishWebSignOut();
        return;
      }
      await endIdpSession(idToken);
    };

    return trackSignOut(signOut());
  },

  ensureCleanSessionForUser: async (newUserId: string) => {
    try {
      const userJson = await AsyncStorage.getItem(USER_KEY);
      if (!userJson) return; // No previous user — nothing to wipe

      let storedUserId: string | null = null;
      try {
        const parsed = JSON.parse(userJson);
        storedUserId = parsed?.id || null;
      } catch {
        // Corrupt stored user JSON — wipe to be safe
        storedUserId = '__corrupt__';
      }

      if (storedUserId === newUserId) return; // Same user, no-op

      // Different user (or corrupt data) — wipe the previous session
      await Promise.all([
        deleteSecureItem(ACCESS_TOKEN_KEY),
        deleteSecureItem(REFRESH_TOKEN_KEY),
        AsyncStorage.removeItem(USER_KEY),
      ]);
      useEncryptionStore.getState().clearSession();
      // clearSession deliberately keeps hasLocalKey / e2eeSetupComplete so a
      // SAME-user re-login does not flash the restore screen. This is a
      // DIFFERENT user: those flags describe the previous account's device
      // state, and left in place they route the new one straight past the
      // recovery prompt.
      useEncryptionStore.setState({
        hasLocalKey: false,
        e2eeSetupComplete: false,
        keyStorageError: null,
        personalTeamId: null,
      });
      useTeamStore.getState().clearTeams();
      queryClient.clear();
      AsyncStorage.removeItem('@cynosure/react-query-cache').catch(() => {});
      set({
        accessToken: null,
        refreshToken: null,
        sessionId: null,
        user: null,
        isAuthenticated: false,
      });
    } catch (error) {
      console.error('Failed to ensure clean session:', error);
      captureException(error, { context: 'authStore.ensureCleanSessionForUser' });
      // Non-fatal: proceed with login even if wipe fails
    }
  },

  initialize: async () => {
    if (IS_WEB) return initializeWeb();
    try {
      const [accessToken, refreshToken, idToken, userJson] = await Promise.all([
        getSecureItem(ACCESS_TOKEN_KEY),
        getSecureItem(REFRESH_TOKEN_KEY),
        getSecureItem(ID_TOKEN_KEY),
        AsyncStorage.getItem(USER_KEY),
      ]);

      let user: AuthUser | null = null;
      if (userJson) {
        try {
          user = JSON.parse(userJson);
        } catch {
          user = null;
        }
      }

      // If an access token exists, check if it's expired by decoding the JWT
      // payload. Enrollment tokens (no refresh token) are still valid sessions.
      // If expired, the axios interceptor will handle refresh on first
      // API call (or force logout if no refresh token is available).
      let isAuthenticated = false;
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          const exp = payload.exp;
          isAuthenticated = !exp || exp * 1000 > Date.now();
        } catch {
          // Malformed token — treat as expired so refresh interceptor handles it
          isAuthenticated = false;
        }
      }

      set({
        accessToken,
        refreshToken,
        idToken,
        user,
        isAuthenticated,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      captureException(error, { context: 'authStore.initialize' });
      set({ isLoading: false });
    }
  },
}));

/**
 * Web start-up: nothing credential-shaped is in storage, so the session — if
 * there is one — is whatever the HttpOnly cookie still carries.
 */
async function initializeWeb(): Promise<void> {
  const { getState, setState } = useAuthStore;
  await purgeLegacyTokens();

  let user: AuthUser | null = null;
  try {
    const userJson = await AsyncStorage.getItem(USER_KEY);
    user = userJson ? JSON.parse(userJson) : null;
  } catch {
    user = null;
  }

  // The callback is about to CREATE the session; a refresh here would race its
  // exchange and could report "signed out" over the top of it.
  if (typeof window !== 'undefined' && window.location.pathname === '/auth/callback') {
    setState({ user, isAuthenticated: false, isLoading: false });
    return;
  }

  try {
    const session = await refreshWebSession();
    // Something else (a sign-in) may have set a session while this awaited.
    if (getState().accessToken) {
      setState({ isLoading: false });
      return;
    }
    setState({
      accessToken: session?.accessToken ?? null,
      sessionId: session?.sessionId ?? null,
      user,
      isAuthenticated: session !== null,
      isLoading: false,
    });
  } catch (error) {
    if (!(error instanceof TransientSessionError)) {
      captureException(error, { context: 'authStore.initializeWeb' });
    }
    // Unreachable API: the session may be perfectly alive. Keep a known user on
    // their cached data, with no access token — the first request 401s into
    // the refresh path, which retries once the network is back. This is what
    // the stored-token start-up used to give an offline user.
    setState({ user, isAuthenticated: user !== null, isLoading: false });
  }
}

